import { FormHelper } from '@presentation/components/FormHelper';
import { calculerParts, messageVeuvage, statutVeuvage } from '@domain/paie/PartsFiscales';
import { parseFlexibleDateToIso } from '@shared/utils/fmtDate';
import { IndemnitesFinContratService } from '@domain/rh/IndemnitesFinContrat';
import type { EnfantDetail, Salarie } from '@shared/types/Salarie';
import { metaPieceIdentite } from '@shared/validation/identifiantsGabon';
import { SALAIRE_BASE_MAX } from '@shared/validation/SalarieSchema';
import { PLAFOND_TRANSPORT_EXONERE, RMM, SMIG } from '@shared/constants/Taux';

export const SALARIE_TAB_DEFS = [
  { id: 'tab-sal-identite', label: 'Identité' },
  { id: 'tab-sal-contrat', label: 'Contrat & rémunération' },
  { id: 'tab-sal-famille', label: 'Famille & fiscalité' },
  { id: 'tab-sal-medecine', label: 'Médecine & sécurité' },
  { id: 'tab-sal-paiement', label: 'Paiement & banque' },
  { id: 'tab-sal-urgence', label: 'Urgence & diplômes' },
  { id: 'tab-sal-sortie', label: 'Sortie' },
] as const;

const CATEGORIE_OPTIONS = [
  { value: 'Direction', label: 'Direction' },
  { value: 'Cadre superieur', label: 'Cadre supérieur' },
  { value: 'Cadre', label: 'Cadre' },
  { value: 'Agent de maitrise', label: 'Agent de maîtrise' },
  { value: 'Employe qualifie', label: 'Employé qualifié' },
  { value: 'Employe', label: 'Employé' },
  { value: 'Ouvrier qualifie', label: 'Ouvrier qualifié' },
  { value: 'Ouvrier', label: 'Ouvrier' },
  { value: 'Stagiaire', label: 'Stagiaire' },
] as const;

const BANQUE_OPTIONS = [
  'BGFI Bank Gabon',
  'BICIG',
  'UBA Gabon',
  'Ecobank Gabon',
  'Orabank Gabon',
  'Citibank Gabon',
  'BHCI Gabon',
  'Société Générale Gabon',
  'Autre',
].map((b) => ({ value: b, label: b }));

function hint(text: string): string {
  return `<p class="form-hint">${text}</p>`;
}

function sectionTitle(text: string): string {
  return `<h4 class="modal-section-title">${text}</h4>`;
}

function displayField(id: string, label: string, value = ''): string {
  return `
    <div class="form-field" data-field="${id}" style="margin-bottom:12px;">
      <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">${label}</label>
      <input id="${id}" type="text" disabled readonly value="${value}" placeholder="—"
        style="width:100%;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;background:#f6f8fa;color:#586069;" />
    </div>
  `;
}

function contratFormValue(existing?: Salarie): string {
  const raw = existing?.statut_contrat;
  if (!raw || raw === 'essai' || raw === 'sortie') return 'CDI';
  return raw;
}

function ouiNonValue(v: boolean | undefined, def: 'oui' | 'non' = 'non'): 'oui' | 'non' {
  if (v === true) return 'oui';
  if (v === false) return 'non';
  return def;
}

function renderEnfantRow(enfant: EnfantDetail, index: number): string {
  return `
    <div class="enfant-row" data-enfant-row data-enfant-index="${index}"
      style="display:grid;grid-template-columns:1.2fr 130px 100px 130px 40px;gap:8px;align-items:center;padding:8px;border:1px solid #eee;border-radius:6px;margin-bottom:8px;">
      ${FormHelper.field('enfant_prenom', 'Prénom', 'text', { value: enfant.prenom, placeholder: 'Prénom' })}
      ${FormHelper.field('enfant_naissance', 'Naissance', 'date', { value: enfant.naissance })}
      ${FormHelper.select(
        'enfant_scolarise',
        'Scolarisé',
        [
          { value: 'oui', label: 'Scolarisé' },
          { value: 'non', label: 'Non scol.' },
        ],
        { value: enfant.scolarise },
      )}
      ${FormHelper.select(
        'enfant_certificat_cnamgs',
        'CNAMGS',
        [
          { value: 'non', label: 'Sans CNAMGS' },
          { value: 'oui', label: 'Cert. CNAMGS' },
        ],
        { value: enfant.certificat_cnamgs ?? 'non' },
      )}
      <button type="button" class="btn btn-sm btn-danger enfant-remove" data-enfant-index="${index}" title="Supprimer">×</button>
    </div>
  `;
}

function buildIdentiteTab(
  existing: Salarie | undefined,
  nextMatricule: string | undefined,
  edit: boolean,
): string {
  const pieceMeta = metaPieceIdentite(existing?.type_piece ?? 'CNI');
  return `
    <div id="tab-sal-identite" class="tab-content active">
      <div class="form-grid">
        ${FormHelper.field('matricule', 'Matricule', 'text', {
          readonly: !edit,
          placeholder: 'M0001',
          value: existing?.matricule ?? nextMatricule,
          maxlength: 50,
        })}
        ${FormHelper.field('date_embauche', 'Date d’embauche', 'date', {
          required: true,
          value: existing?.date_embauche,
          max: new Date().toISOString().slice(0, 10),
        })}
        ${FormHelper.field('nom', 'Nom', 'text', {
          required: true,
          value: existing?.nom,
          maxlength: 100,
          autocomplete: 'family-name',
        })}
        ${FormHelper.field('prenom', 'Prénom', 'text', {
          required: true,
          value: existing?.prenom,
          maxlength: 100,
          autocomplete: 'given-name',
        })}
        ${FormHelper.field('nom_jeune_fille', 'Nom de jeune fille', 'text', {
          value: existing?.nom_jeune_fille,
          placeholder: '(femmes mariées)',
          maxlength: 100,
        })}
        ${FormHelper.select(
          'sexe',
          'Sexe',
          [
            { value: 'M', label: 'M' },
            { value: 'F', label: 'F' },
          ],
          { value: existing?.sexe ?? 'M' },
        )}
        ${FormHelper.field('date_naissance', 'Date de naissance', 'date', {
          value: existing?.date_naissance,
          max: new Date().toISOString().slice(0, 10),
        })}
        ${FormHelper.field('lieu_naissance', 'Lieu de naissance', 'text', {
          value: existing?.lieu_naissance,
          placeholder: 'ex. Libreville, Gabon',
          maxlength: 120,
        })}
        ${FormHelper.field('nationalite', 'Nationalité', 'text', {
          value: existing?.nationalite ?? 'Gabonaise',
          maxlength: 80,
        })}
        ${FormHelper.field('nif', 'NIF personnel (DGI)', 'text', {
          value: existing?.nif,
          placeholder: 'GA0000000000',
          maxlength: 20,
        })}
        ${hint('Recommandé (CGI art. 25)')}
        ${FormHelper.select(
          'type_piece',
          'Type pièce d’identité',
          [
            { value: 'CNI', label: "Carte Nationale d'Identité" },
            { value: 'Passeport', label: 'Passeport' },
            { value: 'Carte sejour', label: 'Carte de séjour (étrangers)' },
          ],
          { value: existing?.type_piece ?? 'CNI' },
        )}
        ${FormHelper.field('cni', pieceMeta.label, 'text', {
          id: 'salarie-cni',
          value: existing?.cni,
          placeholder: pieceMeta.placeholder,
          maxlength: pieceMeta.max,
        })}
        <p class="form-hint" id="salarie-cni-hint">${pieceMeta.hint}</p>
        ${FormHelper.field('piece_delivrance', 'Date délivrance', 'date', {
          value: existing?.piece_delivrance,
        })}
        ${FormHelper.field('piece_validite', 'Date validité (passeport / séjour)', 'date', {
          value: existing?.piece_validite,
        })}
        ${FormHelper.field('piece_autorite', 'Autorité de délivrance', 'text', {
          value: existing?.piece_autorite,
          placeholder: 'ex. Préfecture de l’Estuaire',
          maxlength: 160,
        })}
        ${FormHelper.field('numero_cnss', 'N° immatriculation CNSS', 'text', {
          value: existing?.numero_cnss ?? existing?.cnss,
          maxlength: 40,
        })}
        ${hint('N° CNSS (ou numéro provisoire)')}
        ${FormHelper.field('cnamgs', 'N° CNAMGS', 'text', {
          value: existing?.cnamgs,
          maxlength: 40,
        })}
        ${hint('N° CNAMGS (ou numéro provisoire)')}
        ${FormHelper.field('adresse', 'Adresse personnelle complète', 'text', {
          value: existing?.adresse,
          placeholder: 'ex. Quartier Glass, Rue 18, immeuble Mango n°4',
          maxlength: 200,
        })}
        ${FormHelper.field('ville', 'Quartier / Ville', 'text', {
          value: existing?.ville ?? 'Libreville',
          maxlength: 80,
        })}
        ${FormHelper.field('bp', 'BP postale', 'text', {
          value: existing?.bp,
          placeholder: 'ex. BP 12345',
          maxlength: 30,
        })}
        ${FormHelper.field('email', 'Email professionnel', 'email', {
          required: true,
          value: existing?.email,
          maxlength: 120,
        })}
        ${FormHelper.field('email_perso', 'Email personnel', 'email', {
          value: existing?.email_perso,
          maxlength: 120,
        })}
        ${FormHelper.field('telephone', 'Téléphone', 'tel', {
          value: existing?.telephone,
          placeholder: '+241 074471215',
          maxlength: 20,
          inputmode: 'tel',
        })}
        ${hint('Format libre (ex. +241 074471215)')}
        ${FormHelper.field('telephone_secondaire', 'Téléphone secondaire', 'tel', {
          value: existing?.telephone_secondaire,
          placeholder: '+241 074471215',
          maxlength: 20,
          inputmode: 'tel',
        })}
      </div>
      ${sectionTitle('Carte de séjour (salariés étrangers)')}
      <div class="form-grid">
        ${FormHelper.field('sejour_num', 'N° carte de séjour', 'text', {
          value: existing?.sejour_num,
          maxlength: 40,
        })}
        ${FormHelper.select(
          'sejour_type',
          'Type carte',
          [
            { value: '', label: '—' },
            { value: 'Travailleur salarié', label: 'Travailleur salarié' },
            { value: 'Résident', label: 'Résident' },
            { value: 'Étudiant', label: 'Étudiant' },
            { value: 'Conjoint de Gabonais', label: 'Conjoint de Gabonais' },
          ],
          { value: existing?.sejour_type ?? '' },
        )}
        ${FormHelper.field('sejour_delivrance', 'Date délivrance', 'date', {
          value: existing?.sejour_delivrance,
        })}
        ${FormHelper.field('sejour_expiration', 'Date d’expiration', 'date', {
          value: existing?.sejour_expiration,
        })}
        ${FormHelper.field('permis_travail', 'N° permis de travail', 'text', {
          value: existing?.permis_travail,
          maxlength: 40,
        })}
        ${hint('MTEPS — 8 à 15 caractères alphanumériques')}
        ${FormHelper.select(
          'quota_etranger',
          'Quota étranger validé ?',
          [
            { value: 'na', label: 'N/A' },
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' },
          ],
          { value: existing?.quota_etranger ?? 'na' },
        )}
      </div>
      ${!edit ? hint('Matricule attribué automatiquement (M0001, M0002…) — s’incrémente à chaque nouveau salarié.') : ''}
    </div>
  `;
}

function buildContratTab(existing: Salarie | undefined): string {
  return `
    <div id="tab-sal-contrat" class="tab-content">
      <div class="form-grid">
        ${FormHelper.select(
          'statut_contrat',
          'Type de contrat',
          [
            { value: 'CDI', label: 'CDI' },
            { value: 'CDD', label: 'CDD' },
            { value: 'stage', label: 'Stage' },
            { value: 'apprentissage', label: "Contrat d'apprentissage" },
            { value: 'interim', label: 'Intérim' },
          ],
          { required: true, value: contratFormValue(existing) },
        )}
        ${hint("La période d'essai n'est pas un type de contrat : renseignez sa date de fin ci-contre.")}
        ${FormHelper.field('date_fin_contrat', 'Date de fin (CDD)', 'date', {
          value: existing?.date_fin_contrat,
        })}
        ${FormHelper.field('essai_fin', 'Fin de période d’essai', 'date', { value: existing?.essai_fin })}
        ${FormHelper.select('categorie', 'Catégorie', [...CATEGORIE_OPTIONS], {
          required: true,
          value: existing?.categorie ?? 'Employe',
        })}
        ${FormHelper.field('coefficient', 'Coefficient hiérarchique', 'text', {
          value: existing?.coefficient,
          placeholder: 'ex. 350',
        })}
        ${hint('Convention collective')}
        ${FormHelper.field('poste', 'Fonction', 'text', {
          required: true,
          value: existing?.poste,
          maxlength: 120,
        })}
        ${FormHelper.field('service', 'Service', 'text', { value: existing?.service, maxlength: 120 })}
        ${FormHelper.field('manager', 'Manager direct', 'text', {
          value: existing?.manager,
          maxlength: 120,
        })}
        ${FormHelper.field('convention', 'Convention collective applicable', 'text', {
          value: existing?.convention ?? 'Convention nationale interprofessionnelle',
          maxlength: 200,
        })}
        ${displayField('salarie-preavis', 'Préavis légal (art. 82) — auto par ancienneté')}
        ${FormHelper.field('salaire_base', 'Salaire de base (FCFA)', 'number', {
          required: true,
          min: SMIG,
          max: SALAIRE_BASE_MAX,
          value: existing?.salaire_base,
          inputmode: 'numeric',
        })}
        ${hint(`SMIG ${SMIG.toLocaleString('fr-FR')} FCFA · revenu mensuel minimum ${RMM.toLocaleString('fr-FR')} FCFA (alerte) — entier, max ${SALAIRE_BASE_MAX.toLocaleString('fr-FR')} FCFA`)}
        ${FormHelper.field('sursalaire', 'Sursalaire (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.sursalaire ?? 0,
          inputmode: 'numeric',
        })}
        ${FormHelper.field('indemnite_transport', 'Indemnité transport (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.indemnite_transport ?? 0,
          inputmode: 'numeric',
        })}
        ${hint(`Au-delà de ${PLAFOND_TRANSPORT_EXONERE.toLocaleString('fr-FR')} F / mois (prorata jours / 30), l’excédent entre dans le brut CNSS, hors CNAMGS.`)}
        ${FormHelper.field('indemnite_vehicule', 'Indemnité véhicule (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.indemnite_vehicule ?? 0,
          inputmode: 'numeric',
        })}
        ${hint(`Même plafond que le transport : au-delà de ${PLAFOND_TRANSPORT_EXONERE.toLocaleString('fr-FR')} F, l’excédent est soumis CNSS uniquement.`)}
        ${FormHelper.field('indemnite_logement', 'Indemnité logement (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.indemnite_logement ?? 0,
          inputmode: 'numeric',
        })}
        ${hint('Indemnité (CNSS seul). Une prime de logement imposable se saisit en Prime à la paie, pas ici.')}
        ${FormHelper.field('indemnite_fonction', 'Indemnité fonction (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.indemnite_fonction ?? 0,
          inputmode: 'numeric',
        })}
        ${FormHelper.field('indemnite_autres', 'Autres indemnités (FCFA)', 'number', {
          min: 0,
          max: SALAIRE_BASE_MAX,
          value: existing?.indemnite_autres ?? 0,
          inputmode: 'numeric',
        })}
        ${FormHelper.field('centre_cout', 'Centre de coût', 'text', {
          value: existing?.centre_cout,
          placeholder: 'Ex. ADM, COMM…',
        })}
      </div>
    </div>
  `;
}

function buildFamilleTab(existing: Salarie | undefined): string {
  const enfants = existing?.enfants ?? existing?.nb_enfants ?? 0;
  const situationInit = existing?.situation ?? existing?.situation_familiale;
  const partsInit = calculerParts({
    situation: situationInit,
    enfants,
    conjoint_actif: existing?.conjoint_salarie ?? existing?.conjoint_actif ?? false,
    date_deces_conjoint: existing?.date_deces_conjoint,
  });
  const veufInit = situationInit === 'veuf';
  const enfantsHtml = (existing?.enfants_detail ?? [])
    .map((e, i) => renderEnfantRow(e, i))
    .join('');

  return `
    <div id="tab-sal-famille" class="tab-content">
      <div class="form-grid">
        ${FormHelper.select(
          'situation',
          'Situation matrimoniale',
          [
            { value: 'celibataire', label: 'Célibataire' },
            { value: 'marie', label: 'Marié(e)' },
            { value: 'pacs', label: 'Pacsé(e)' },
            { value: 'divorce', label: 'Divorcé(e)' },
            { value: 'veuf', label: 'Veuf(ve)' },
          ],
          {
            value: existing?.situation ?? existing?.situation_familiale ?? 'celibataire',
          },
        )}
        ${FormHelper.select(
          'regime_matrimonial',
          'Régime matrimonial',
          [
            { value: '', label: '— (si marié)' },
            { value: 'Communauté légale', label: 'Communauté légale' },
            { value: 'Séparation de biens', label: 'Séparation de biens' },
            { value: 'Participation aux acquêts', label: 'Participation aux acquêts' },
          ],
          { value: existing?.regime_matrimonial ?? '' },
        )}
        ${FormHelper.field('date_mariage', 'Date de mariage', 'date', { value: existing?.date_mariage })}
        <div id="salarie-veuvage-zone" ${veufInit ? '' : 'hidden'}>
          ${FormHelper.field('date_deces_conjoint', 'Date du décès du conjoint', 'date', {
            value: existing?.date_deces_conjoint,
          })}
          <p class="form-hint" id="salarie-veuvage-aide" role="status" style="color:var(--ardoise);">${messageVeuvage(
            statutVeuvage(situationInit, existing?.date_deces_conjoint, new Date().toISOString().slice(0, 10)),
          )}</p>
        </div>
        ${FormHelper.field('enfants', 'Nombre d’enfants à charge', 'number', {
          min: 0,
          max: 20,
          value: enfants,
        })}
        ${hint('Parts fiscales : +0,5 par enfant, +0,5 parent isolé, plafond 5 (célibataire 1 enfant = 2,0 ; marié 3 enfants = 3,5).')}
        ${displayField('salarie-parts', 'Parts fiscales calculées', partsInit.toFixed(1).replace('.', ','))}
        ${FormHelper.field('personnes_charge', 'Autres personnes à charge', 'number', {
          min: 0,
          max: 10,
          value: existing?.personnes_charge ?? 0,
          placeholder: 'parents, etc.',
        })}
      </div>
      ${sectionTitle('Conjoint')}
      <div class="form-grid">
        ${FormHelper.field('conjoint_nom', 'Nom conjoint', 'text', { value: existing?.conjoint_nom })}
        ${FormHelper.field('conjoint_prenom', 'Prénom conjoint', 'text', {
          value: existing?.conjoint_prenom,
        })}
        ${FormHelper.field('conjoint_nif', 'NIF conjoint', 'text', { value: existing?.conjoint_nif })}
        ${FormHelper.field('conjoint_profession', 'Profession conjoint', 'text', {
          value: existing?.conjoint_profession,
        })}
        ${FormHelper.select(
          'conjoint_salarie',
          'Conjoint salarié?',
          [
            { value: 'non', label: 'Non' },
            { value: 'oui', label: 'Oui' },
          ],
          {
            value: ouiNonValue(existing?.conjoint_salarie ?? existing?.conjoint_actif, 'non'),
          },
        )}
        ${FormHelper.select(
          'conjoint_cnamgs',
          'Conjoint à charge CNAMGS',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' },
          ],
          { value: ouiNonValue(existing?.conjoint_cnamgs, 'oui') },
        )}
        ${hint('Loi CNAMGS Art. 8')}
      </div>
      ${sectionTitle('Enfants à charge (détail)')}
      <div id="salarie-enfants-detail">${enfantsHtml || '<p class="form-hint">Aucun enfant ajouté.</p>'}</div>
      <button type="button" id="salarie-add-enfant" class="btn btn-sm btn-ghost" style="margin-top:8px;">+ Ajouter un enfant</button>
    </div>
  `;
}

function buildMedecineTab(existing: Salarie | undefined): string {
  return `
    <div id="tab-sal-medecine" class="tab-content">
      <div class="form-grid">
        ${FormHelper.field('medecine_centre', 'Centre médecine du travail', 'text', {
          value: existing?.medecine_centre,
          placeholder: 'ex. ONMT Libreville',
        })}
        ${hint('Loi 3/94 Art. 121')}
        ${FormHelper.field('medecin_referent', 'Médecin référent', 'text', {
          value: existing?.medecin_referent,
        })}
        ${FormHelper.field('visite_derniere', 'Date dernière visite médicale', 'date', {
          value: existing?.visite_derniere,
        })}
        ${FormHelper.field('visite_prochaine', 'Date prochaine visite', 'date', {
          value: existing?.visite_prochaine,
        })}
        ${hint('Annuelle obligatoire')}
        ${FormHelper.select(
          'aptitude',
          'Aptitude au poste',
          [
            { value: 'apte', label: 'Apte sans restriction' },
            { value: 'apte_restriction', label: 'Apte avec restrictions' },
            { value: 'inapte_temporaire', label: 'Inapte temporaire' },
            { value: 'inapte_definitif', label: 'Inapte définitif' },
          ],
          { value: existing?.aptitude ?? 'apte' },
        )}
        ${FormHelper.select(
          'groupe_sanguin',
          'Groupe sanguin',
          [
            { value: '', label: '—' },
            { value: 'A+', label: 'A+' },
            { value: 'A-', label: 'A-' },
            { value: 'B+', label: 'B+' },
            { value: 'B-', label: 'B-' },
            { value: 'AB+', label: 'AB+' },
            { value: 'AB-', label: 'AB-' },
            { value: 'O+', label: 'O+' },
            { value: 'O-', label: 'O-' },
          ],
          { value: existing?.groupe_sanguin ?? '' },
        )}
        ${FormHelper.field(
          'restrictions_medicales',
          'Restrictions médicales / aménagements',
          'text',
          {
            value: existing?.restrictions_medicales,
            placeholder: 'ex. pas de port de charges lourdes',
          },
        )}
        ${FormHelper.field('vaccinations', 'Vaccinations à jour', 'text', {
          value: existing?.vaccinations,
          placeholder: 'ex. Fièvre jaune (2024), Hépatite B (2023)',
        })}
        ${FormHelper.select(
          'vaccins_archive',
          'Carnet de vaccination archivé',
          [
            { value: 'non', label: 'Non' },
            { value: 'oui', label: 'Oui' },
          ],
          { value: ouiNonValue(existing?.vaccins_archive, 'non') },
        )}
        ${FormHelper.select(
          'medecine_ok',
          'Visite médecine périodique respectée',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non - alerter' },
          ],
          { value: ouiNonValue(existing?.medecine_ok, 'oui') },
        )}
      </div>
    </div>
  `;
}

function buildPaiementTab(existing: Salarie | undefined): string {
  return `
    <div id="tab-sal-paiement" class="tab-content">
      <div class="form-grid">
        ${FormHelper.select(
          'mode_paiement',
          'Mode de paiement',
          [
            { value: 'virement', label: 'Virement bancaire' },
            { value: 'cheque', label: 'Chèque' },
            { value: 'especes', label: 'Espèces' },
            { value: 'mobile_money', label: 'Mobile Money' },
          ],
          { value: existing?.mode_paiement ?? 'virement' },
        )}
        ${FormHelper.select('banque', 'Banque', [{ value: '', label: '—' }, ...BANQUE_OPTIONS], {
          value: existing?.banque ?? '',
        })}
        ${FormHelper.field('agence', 'Agence', 'text', {
          value: existing?.agence,
          placeholder: 'ex. Agence Bord de mer',
        })}
        ${FormHelper.field('code_banque', 'Code banque', 'text', { value: existing?.code_banque })}
        ${FormHelper.field('code_guichet', 'Code guichet', 'text', { value: existing?.code_guichet })}
        ${FormHelper.field('num_compte', 'N° compte', 'text', { value: existing?.num_compte })}
        ${FormHelper.field('cle_rib', 'Clé RIB', 'text', { value: existing?.cle_rib })}
        ${FormHelper.field('iban', 'IBAN complet', 'text', {
          value: existing?.iban,
          placeholder: 'GA21 10001 0000 1234567890 28',
        })}
        ${FormHelper.field('bic', 'BIC / SWIFT', 'text', { value: existing?.bic })}
        ${FormHelper.field('mobile_money', 'N° Mobile Money', 'text', {
          value: existing?.mobile_money,
          placeholder: '(si applicable)',
        })}
      </div>
    </div>
  `;
}

function buildUrgenceTab(existing: Salarie | undefined): string {
  return `
    <div id="tab-sal-urgence" class="tab-content">
      ${sectionTitle("Personne à prévenir en cas d'urgence")}
      <div class="form-grid">
        ${FormHelper.field('contact_urgence_nom', 'Nom complet', 'text', {
          value: existing?.contact_urgence_nom,
        })}
        ${FormHelper.select(
          'contact_urgence_lien',
          'Lien',
          [
            { value: 'Conjoint', label: 'Conjoint' },
            { value: 'Père', label: 'Père' },
            { value: 'Mère', label: 'Mère' },
            { value: 'Frère', label: 'Frère' },
            { value: 'Sœur', label: 'Sœur' },
            { value: 'Enfant', label: 'Enfant' },
            { value: 'Ami(e) proche', label: 'Ami(e) proche' },
            { value: 'Autre', label: 'Autre' },
          ],
          { value: existing?.contact_urgence_lien ?? 'Conjoint' },
        )}
        ${FormHelper.field('contact_urgence_telephone', 'Téléphone principal', 'tel', {
          value: existing?.contact_urgence_telephone,
          placeholder: '+241 074471215',
          maxlength: 20,
          inputmode: 'tel',
        })}
        ${FormHelper.field('contact_urgence_telephone_2', 'Téléphone secondaire', 'tel', {
          value: existing?.contact_urgence_telephone_2,
          placeholder: '+241 074471215',
          maxlength: 20,
          inputmode: 'tel',
        })}
        ${FormHelper.field('contact_urgence_adresse', 'Adresse', 'text', {
          value: existing?.contact_urgence_adresse,
          maxlength: 200,
        })}
      </div>
      ${sectionTitle('Diplômes et formations')}
      <div class="form-grid">
        ${FormHelper.field('diplome', 'Diplôme principal', 'text', {
          value: existing?.diplome,
          placeholder: 'ex. Master en Gestion',
        })}
        ${FormHelper.field('ecole', 'École / Université', 'text', {
          value: existing?.ecole,
          placeholder: 'ex. INSG Libreville',
        })}
        ${FormHelper.field('annee_diplome', 'Année d’obtention', 'number', {
          min: 1950,
          max: 2030,
          value: existing?.annee_diplome,
        })}
        ${FormHelper.select(
          'niveau_etudes',
          'Niveau d’études',
          [
            { value: '', label: '—' },
            { value: 'Sans diplôme', label: 'Sans diplôme' },
            { value: 'CEP', label: 'CEP' },
            { value: 'BEPC / BEP', label: 'BEPC / BEP' },
            { value: 'Baccalauréat', label: 'Baccalauréat' },
            { value: 'Bac+2 (BTS / DUT)', label: 'Bac+2 (BTS / DUT)' },
            { value: 'Bac+3 (Licence)', label: 'Bac+3 (Licence)' },
            { value: 'Bac+5 (Master / Ingénieur)', label: 'Bac+5 (Master / Ingénieur)' },
            { value: 'Bac+8 (Doctorat)', label: 'Bac+8 (Doctorat)' },
          ],
          { value: existing?.niveau_etudes ?? '' },
        )}
        ${FormHelper.field('certifications', 'Certifications professionnelles', 'text', {
          value: existing?.certifications,
          placeholder: 'ex. PMP, ITIL, CISA',
        })}
        ${FormHelper.select(
          'niveau_anglais',
          'Niveau d’anglais',
          [
            { value: '', label: '— Non renseigné —' },
            { value: 'aucun', label: 'Aucun' },
            { value: 'notions', label: 'Notions' },
            { value: 'intermediaire', label: 'Intermédiaire' },
            { value: 'courant', label: 'Courant' },
            { value: 'bilingue', label: 'Bilingue / Natif' },
          ],
          { value: existing?.niveau_anglais ?? '' },
        )}
        ${hint('Donnée structurée, exploitable en pilotage.')}
        ${FormHelper.field('langues', 'Autres langues (libre)', 'text', {
          value: existing?.langues,
          placeholder: 'ex. Français (natif), Fang (notion)',
        })}
      </div>
    </div>
  `;
}

function buildSortieTab(existing: Salarie | undefined, edit: boolean): string {
  const estSorti = existing?.statut === 'sortie' || existing?.statut === 'inactif';
  return `
    <div id="tab-sal-sortie" class="tab-content">
      <div class="form-grid">
        ${
          edit
            ? FormHelper.select(
                'statut',
                'Statut',
                [
                  { value: 'actif', label: 'Actif' },
                  { value: 'suspendu', label: 'Suspendu (inactif, présent dans l’entreprise)' },
                  { value: 'conge', label: 'Congé' },
                  { value: 'inactif', label: 'Inactif' },
                  { value: 'sortie', label: 'Ancien employé (sorti des effectifs)' },
                ],
                { required: true, value: existing?.statut ?? 'actif' },
              )
            : ''
        }
        ${FormHelper.field('date_sortie', 'Date de sortie des effectifs', 'date', {
          value: existing?.date_sortie,
        })}
        ${FormHelper.select(
          'motif_sortie',
          'Motif',
          [
            { value: '', label: '—' },
            { value: 'Démission', label: 'Démission' },
            { value: 'Licenciement', label: 'Licenciement' },
            { value: 'Fin de CDD', label: 'Fin de CDD' },
            { value: 'Rupture conventionnelle', label: 'Rupture conventionnelle' },
            { value: 'Départ retraite', label: 'Départ retraite' },
            { value: 'Décès', label: 'Décès' },
            { value: 'Inaptitude médicale', label: 'Inaptitude médicale' },
            { value: 'Faute grave', label: 'Faute grave' },
          ],
          { value: existing?.motif_sortie ?? '' },
        )}
        ${FormHelper.field('conges_solde', 'Solde de congés à payer (jours)', 'number', {
          min: 0,
          value: existing?.conges_solde,
          placeholder: 'vide = calcul automatique',
        })}
        ${hint('Laisser vide pour calculer depuis les congés acquis/pris')}
        ${FormHelper.field('motif_detail', 'Commentaire', 'text', { value: existing?.motif_detail })}
        ${
          edit
            ? FormHelper.field('date_reintegration', 'Date de réintégration', 'date', {
                value: existing?.date_reintegration,
              })
            : ''
        }
        ${
          edit
            ? hint(
                estSorti
                  ? 'Pour réintégrer : passez le statut à Actif et renseignez la date de réintégration (stages, CDD, MAD…).'
                  : 'Renseignez une date de réintégration uniquement en cas de retour après une sortie.',
              )
            : ''
        }
      </div>
      <div class="banner" style="margin-top:16px;font-size:12px;">
        Le passage au statut <strong>Ancien employé</strong> déclenche automatiquement le calcul du solde de tout compte selon les
        <strong>paramètres définis dans Paramètres › Solde de tout compte</strong> (barème de licenciement, base de calcul, congés réellement pris, préavis, précarité CDD, 13e mois).
        Le détail est aussi disponible dans <strong>Conformité › Solde de tout compte</strong>.
      </div>
    </div>
  `;
}

export function buildSalarieFormTabsHtml(existing?: Salarie, nextMatricule?: string): string {
  const edit = Boolean(existing);
  const tabsHtml = SALARIE_TAB_DEFS.map(
    (t, i) =>
      `<button type="button" class="tab${i === 0 ? ' active' : ''}" data-tab="${t.id}">${t.label}</button>`,
  ).join('');

  return `
    <div class="tabs salarie-form-tabs">${tabsHtml}</div>
    ${buildIdentiteTab(existing, nextMatricule, edit)}
    ${buildContratTab(existing)}
    ${buildFamilleTab(existing)}
    ${buildMedecineTab(existing)}
    ${buildPaiementTab(existing)}
    ${buildUrgenceTab(existing)}
    ${buildSortieTab(existing, edit)}
  `;
}

function parseOuiNon(raw: string | undefined): boolean {
  return raw === 'oui' || raw === 'true' || raw === '1';
}

function readSelect(form: HTMLFormElement, name: string): string {
  return form.querySelector<HTMLSelectElement>(`[name="${name}"]`)?.value ?? '';
}

export function collectEnfantsDetail(container: HTMLElement): EnfantDetail[] {
  const rows = container.querySelectorAll<HTMLElement>('[data-enfant-row]');
  return Array.from(rows)
    .map((row) => ({
      prenom: row.querySelector<HTMLInputElement>('[name="enfant_prenom"]')?.value.trim() ?? '',
      naissance: row.querySelector<HTMLInputElement>('[name="enfant_naissance"]')?.value ?? '',
      scolarise: (row.querySelector<HTMLSelectElement>('[name="enfant_scolarise"]')?.value ??
        'oui') as 'oui' | 'non',
      certificat_cnamgs: (row.querySelector<HTMLSelectElement>('[name="enfant_certificat_cnamgs"]')
        ?.value ?? 'non') as 'oui' | 'non',
    }))
    .filter((e) => e.prenom || e.naissance);
}

export interface SalarieFormExtras {
  enfants_detail: EnfantDetail[];
  conjoint_salarie: boolean;
  conjoint_cnamgs: boolean;
  vaccins_archive: boolean;
  medecine_ok: boolean;
  quota_etranger: 'oui' | 'non' | 'na';
  cnss: string | undefined;
}

export function parseSalarieFormExtras(
  form: HTMLFormElement,
  container: HTMLElement,
): SalarieFormExtras {
  const numeroCnss = form.querySelector<HTMLInputElement>('[name="numero_cnss"]')?.value.trim();
  return {
    enfants_detail: collectEnfantsDetail(container),
    conjoint_salarie: parseOuiNon(readSelect(form, 'conjoint_salarie')),
    conjoint_cnamgs: parseOuiNon(readSelect(form, 'conjoint_cnamgs')),
    vaccins_archive: parseOuiNon(readSelect(form, 'vaccins_archive')),
    medecine_ok: parseOuiNon(readSelect(form, 'medecine_ok')),
    quota_etranger: (readSelect(form, 'quota_etranger') || 'na') as 'oui' | 'non' | 'na',
    cnss: numeroCnss || undefined,
  };
}

function refreshPartsField(form: HTMLFormElement): void {
  const partsEl = form.querySelector<HTMLInputElement>('#salarie-parts');
  if (!partsEl) return;
  const situation = readSelect(form, 'situation') as Salarie['situation'];
  const conjointSalarie = parseOuiNon(readSelect(form, 'conjoint_salarie'));
  const enfants = Number(form.querySelector<HTMLInputElement>('[name="enfants"]')?.value ?? 0) || 0;
  const dateDeces = lireDateDecesConjoint(form);
  const parts = calculerParts({
    situation,
    enfants,
    conjoint_actif: conjointSalarie,
    date_deces_conjoint: dateDeces,
  });
  partsEl.value = parts.toFixed(1).replace('.', ',');
  refreshVeuvage(form, situation, dateDeces);
}

/** Date du décès du conjoint saisie (JJ/MM/AAAA ou calendrier) au format AAAA-MM-JJ, ou undefined. */
function lireDateDecesConjoint(form: HTMLFormElement): string | undefined {
  const brut = form.querySelector<HTMLInputElement>('[name="date_deces_conjoint"]')?.value ?? '';
  return parseFlexibleDateToIso(brut) ?? undefined;
}

/** Affiche la date du décès seulement pour un(e) veuf(ve) et explique l'effet sur les parts. */
function refreshVeuvage(form: HTMLFormElement, situation: Salarie['situation'], dateDeces: string | undefined): void {
  const zone = form.querySelector<HTMLElement>('#salarie-veuvage-zone');
  if (!zone) return;
  zone.hidden = situation !== 'veuf';
  const aide = form.querySelector<HTMLElement>('#salarie-veuvage-aide');
  if (aide) {
    aide.textContent = messageVeuvage(statutVeuvage(situation, dateDeces, new Date().toISOString().slice(0, 10)));
  }
}

function refreshPreavisField(form: HTMLFormElement): void {
  const preavisEl = form.querySelector<HTMLInputElement>('#salarie-preavis');
  if (!preavisEl) return;
  const dateEmbauche = form.querySelector<HTMLInputElement>('[name="date_embauche"]')?.value ?? '';
  if (!dateEmbauche) {
    preavisEl.value = '—';
    return;
  }
  const { duree, unite } = IndemnitesFinContratService.dureePreavis({
    date_embauche: dateEmbauche,
  });
  const valeur = Math.round(duree * 10) / 10;
  preavisEl.value = `${valeur} ${unite === 'mois' ? 'mois' : 'jours'}`;
}

function reindexEnfantRows(container: HTMLElement): void {
  const detail = container.querySelector('#salarie-enfants-detail');
  if (!detail) return;
  detail.querySelectorAll<HTMLElement>('[data-enfant-row]').forEach((row, i) => {
    row.dataset.enfantIndex = String(i);
    row
      .querySelector<HTMLButtonElement>('.enfant-remove')
      ?.setAttribute('data-enfant-index', String(i));
  });
}

function renderEnfantsDetail(container: HTMLElement, enfants: EnfantDetail[]): void {
  const detail = container.querySelector('#salarie-enfants-detail');
  if (!detail) return;
  if (enfants.length === 0) {
    detail.innerHTML = '<p class="form-hint">Aucun enfant ajouté.</p>';
    return;
  }
  detail.innerHTML = enfants.map((e, i) => renderEnfantRow(e, i)).join('');
}

export function wireSalarieFormDynamics(
  container: HTMLElement,
  form: HTMLFormElement,
  existing?: Salarie,
): void {
  const tabs = container.querySelectorAll<HTMLButtonElement>('.salarie-form-tabs .tab');
  tabs.forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabId = btn.dataset.tab;
      if (!tabId) return;
      tabs.forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      container.querySelectorAll('.tab-content').forEach((panel) => {
        panel.classList.toggle('active', panel.id === tabId);
      });
    });
  });

  const typePiece = form.querySelector<HTMLSelectElement>('[name="type_piece"]');
  const cniInput = form.querySelector<HTMLInputElement>('[name="cni"]');
  const cniLabel = form.querySelector<HTMLElement>('#cni-label');
  const cniHint = form.querySelector<HTMLElement>('#salarie-cni-hint');
  const refreshPieceMeta = (): void => {
    const meta = metaPieceIdentite(typePiece?.value);
    if (cniLabel) {
      cniLabel.textContent = meta.label;
    }
    if (cniInput) {
      cniInput.placeholder = meta.placeholder;
      cniInput.maxLength = meta.max;
    }
    if (cniHint) cniHint.textContent = meta.hint;
  };
  typePiece?.addEventListener('change', refreshPieceMeta);
  refreshPieceMeta();

  // Filtre saisie téléphone : chiffres, + et espaces (ex. +241 074471215)
  form.querySelectorAll<HTMLInputElement>('input[type="tel"]').forEach((tel) => {
    tel.addEventListener('input', () => {
      const cleaned = tel.value.replace(/[^\d+\s().-]/g, '');
      if (cleaned !== tel.value) tel.value = cleaned;
    });
  });

  // Salaire : entiers uniquement
  form.querySelectorAll<HTMLInputElement>('input[type="number"]').forEach((num) => {
    num.addEventListener('input', () => {
      if (num.value.includes('.') || num.value.includes(',')) {
        num.value = num.value.replace(/[.,].*$/, '');
      }
    });
  });

  const contrat = form.querySelector<HTMLSelectElement>('[name="statut_contrat"]');
  const fin = form.querySelector<HTMLInputElement>('[name="date_fin_contrat"]');
  const refreshFin = (): void => {
    const determine =
      contrat?.value === 'CDD' ||
      contrat?.value === 'stage' ||
      contrat?.value === 'apprentissage' ||
      contrat?.value === 'interim';
    if (fin?.closest('.form-field')) {
      (fin.closest('.form-field') as HTMLElement).style.display = determine ? '' : 'none';
    }
    if (fin) {
      fin.required = determine;
      if (!determine) fin.value = '';
    }
  };
  contrat?.addEventListener('change', refreshFin);
  refreshFin();

  const visiteProchaine = form.querySelector<HTMLInputElement>('[name="visite_prochaine"]');
  if (visiteProchaine) {
    visiteProchaine.min = new Date().toISOString().slice(0, 10);
  }

  const categorie = form.querySelector<HTMLSelectElement>('[name="categorie"]');
  categorie?.addEventListener('change', () => refreshPreavisField(form));
  form
    .querySelector('[name="date_embauche"]')
    ?.addEventListener('change', () => refreshPreavisField(form));
  refreshPreavisField(form);

  for (const name of ['situation', 'conjoint_salarie', 'enfants', 'date_deces_conjoint']) {
    form
      .querySelector(`[name="${name}"]`)
      ?.addEventListener('change', () => refreshPartsField(form));
    form
      .querySelector(`[name="${name}"]`)
      ?.addEventListener('input', () => refreshPartsField(form));
  }
  refreshPartsField(form);

  const enfantsCourants: EnfantDetail[] = existing?.enfants_detail
    ? [...existing.enfants_detail]
    : [];

  container.querySelector('#salarie-add-enfant')?.addEventListener('click', () => {
    enfantsCourants.push({
      prenom: '',
      naissance: '',
      scolarise: 'oui',
      certificat_cnamgs: 'non',
    });
    renderEnfantsDetail(container, enfantsCourants);
    reindexEnfantRows(container);
  });

  container.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.enfant-remove');
    if (!btn) return;
    const idx = Number(btn.dataset.enfantIndex);
    if (!Number.isFinite(idx)) return;
    enfantsCourants.splice(idx, 1);
    renderEnfantsDetail(container, enfantsCourants);
    reindexEnfantRows(container);
  });
}
